import { createContext, useContext, useMemo, useReducer } from "react"
import { produce } from "immer"
import { usePreferences } from "hooks/use-preferences"

const EMPTY_OBJECT = {}

export const GraphContext = createContext()

const PREFERENCE_SUBTYPE = "graph"

const DEFAULT_STATE = {
  selectedTemplate: "performance",
  disabledItems: EMPTY_OBJECT,
}

const PREFERENCE_FIELDS = [
  "selectedTemplate",
  "disabledItems",
]

function reducer(current, action) {
  return produce(current, draft => {
    switch (action.type) {
      case "setSelectedTemplate":
        draft.selectedTemplate = action.template
        break
      case "setDisabledItems": {
        const currentState = draft.disabledItems[action.itemId]
        draft.disabledItems[action.itemId] = currentState === undefined ? false : !currentState
        break
      }
      case "setOnlyItem": {
        const allItemIds = action.allItemIds || []
        const targetItemId = action.itemId

        draft.disabledItems = Object.fromEntries(
          allItemIds.map(itemId => [itemId, itemId !== targetItemId]),
        )
        break
      }
      case "enableAllItems": {
        const itemIds = action.allItemIds || []

        Object.assign(
          draft.disabledItems,
          Object.fromEntries(itemIds.map(itemId => [itemId, false])),
        )
        break
      }
      case "setPreferences":
        if (action.preferences) {
          Object.assign(draft, action.preferences)
        }
        break
    }
  })
}

function setPreferences(preferences) {
  return { type: "setPreferences", preferences }
}

export default function useGraphContext(parentContext) {
  const parentContextValue = useContext(parentContext)
  const [state, dispatch] = useReducer(reducer, DEFAULT_STATE)

  const isPreferencesLoaded = usePreferences(
    PREFERENCE_SUBTYPE,
    PREFERENCE_FIELDS,
    state,
    dispatch,
    setPreferences,
  )

  const contextUpdaters = useMemo(() => ({
    setSelectedTemplate: (template) => dispatch({ type: "setSelectedTemplate", template }),
    setDisabledItems: (itemId) => dispatch({ type: "setDisabledItems", itemId }),
    setOnlyItem: (itemId, allItemIds) => dispatch({ type: "setOnlyItem", itemId, allItemIds }),
    enableAllItems: (allItemIds) => dispatch({ type: "enableAllItems", allItemIds }),
  }), [dispatch])

  const contextValue = useMemo(() => ({
    ...parentContextValue,
    ...state,
    ...contextUpdaters,
    isPreferencesLoaded,
  }), [parentContextValue, state, contextUpdaters, isPreferencesLoaded])

  return [GraphContext, contextValue]
}
